import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of, switchMap, throwError } from 'rxjs';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { signInWithPopup, getAuth, GoogleAuthProvider } from 'firebase/auth';
import { collection, doc, setDoc } from "firebase/firestore";
import {ActivatedRoute, Router} from '@angular/router';
import {User} from '../user/user.types';
import {
  Auth,
  createUserWithEmailAndPassword, sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut
} from '@angular/fire/auth';
import { getDoc, getDocs, getFirestore, serverTimestamp } from "firebase/firestore";
import {UserCredential} from "@firebase/auth";
import {DateTime} from "luxon";
import Utils from "../../helpers/utils";



@Injectable()
export class AuthService
{
    private _authenticated: boolean = false;
    public user: User;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _router: Router,
        private _activatedRoute: ActivatedRoute,
        private _fireAuth: Auth
        //public angularFireAuth: AngularFireAuth,
    )
    {
        const db = getFirestore();
        this._fireAuth.onAuthStateChanged(async (user) => {
            if (user && user.emailVerified) {
                console.log('user: ' + user);
                console.log('user: ' + user['accessToken']);

                /*
                const userRef = collection(db, "users");
                const docSnap = await getDocs(userRef);
                docSnap.forEach((doc) => {
                  // doc.data() is never undefined for query doc snapshots
                  console.log(doc.id, " => ", doc.data());
                });
                 */


                const userRef = doc(db, "users", user.uid);
                let createdAt = serverTimestamp();
                let isSuperAdmin = false;
                const userSnapShot = await getDoc(userRef);
                if (userSnapShot.exists()) {
                  const userData = userSnapShot.data();
                  createdAt = userData.createdAt ? userData.createdAt : serverTimestamp();
                  isSuperAdmin = userData.isSuperAdmin ? userData.isSuperAdmin : false;
                } else {
                   console.log("User does not exist");
                }

                const data = {
                    id: user.uid,
                    email: user.email,
                    name: user.displayName,
                    emailVerified: user.emailVerified,
                    photoURL: user.photoURL,
                    createdAt: createdAt,
                    isSuperAdmin: isSuperAdmin,
                    lastLoginAt: serverTimestamp(),
                };

                const userToUpdateRef = collection(db, "users");
                await setDoc(doc(userToUpdateRef, user.uid), data, {merge: true});

                // Set the authenticated flag to true
                this._authenticated = true;
                const globalUser: User = {
                    id: data.id,
                    name: data.name,
                    email: data.email,
                    emailVerified: data.emailVerified,
                    token: user['accessToken'],
                    isSuperAdmin: data.isSuperAdmin,
                    avatar: data.photoURL,
                    tokenExpireAt: Utils.getIdTokenExpirationDateTime()
                };
                this._userService.user =  globalUser;
                this.user = globalUser;
                const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || '/signed-in-redirect';

                // Navigate to the redirect url
                this._router.navigateByUrl(redirectURL);
            }
        });
        /*
        const auth = getAuth();

        signOut(auth).then(() => {
          // Sign-out successful.
        }).catch((error) => {
          // An error happened.
        });

         */

        /*
        this.angularFireAuth.authState.subscribe(async (user) => {
            console.log('user: ' + user);
    });*/
    }
    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string)
    {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string
    {
        return localStorage.getItem('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    public async forgotPassword(email: string): Promise<void>
    {
      const auth = getAuth();
      return sendPasswordResetEmail(auth, email)
      //return this._fireAuth.auth().sendPasswordResetEmail(email);
      //return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any>
    {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    public async signIn(credentials: { email: string; password: string }): Promise<any>
    {
        const auth = getAuth();
        return signInWithEmailAndPassword(auth, credentials.email, credentials.password)
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any>
    {
        // Sign in using the token
        return this._httpClient.post('api/auth/sign-in-with-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError(() =>

                // Return false
                of(false)
            ),
            switchMap((response: any) => {

                // Replace the access token with the new one if it's available on
                // the response object.
                //
                // This is an added optional step for better security. Once you sign
                // in using the token, you should generate a new one on the server
                // side and attach it to the response object. Then the following
                // piece of code can replace the token with the refreshed one.
                if ( response.accessToken )
                {
                    this.accessToken = response.accessToken;
                }

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any>
    {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');
        this._fireAuth.signOut();

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any>
    {
      const auth = getAuth();
      createUserWithEmailAndPassword(auth, user.email, user.password)
        .then((userCredential) => {
          // Signed in
          const user = userCredential.user;
          if (!userCredential.user.emailVerified) {
            sendEmailVerification(user).then(() => {
                return this._router.navigateByUrl('/confirmation-required');
            });
          } else {
              this._router.navigateByUrl('/signed-in-redirect');
          }

          // ...
        })
        .catch((error) => {
          const errorCode = error.code;
          const errorMessage = error.message;
          // ..
        });
        return this._httpClient.post('api/auth/sign-up', user);
    }

    resendConfirmationEmail() {
      const auth = getAuth();
      return sendEmailVerification(auth.currentUser)
    }



    googleLogin() {
        const provider = new GoogleAuthProvider();
        const auth = getAuth();
        return signInWithPopup(auth, provider).then((result) => {
            // This gives you a Google Access Token. You can use it to access the Google API.
            /*const credential = GoogleAuthProvider.credentialFromResult(result);
            const token = credential.accessToken;
            // The signed-in user info.
            const user = result.user;
            // Store the access token in the local storage
            //this.accessToken = token;

            // Set the authenticated flag to true
            this._authenticated = true;

            // Store the user on the user service
            const user2: User = {
                id:'id',
                name:'name',
                email:'emails'
            };
            this._userService.user =  user2;
            const redirectURL = this._activatedRoute.snapshot.queryParamMap.get('redirectURL') || '/signed-in-redirect';

            // Navigate to the redirect url
            this._router.navigateByUrl(redirectURL);*/
            // ...
          }).catch((error) => {
            // Handle Errors here.
            const errorCode = error.code;
            const errorMessage = error.message;
            // The email of the user's account used.
            const email = error.customData.email;
            // The AuthCredential type that was used.
            const credential = GoogleAuthProvider.credentialFromError(error);
            // ...
          });
    }
    /*
    private oAuthLogin(provider: auth.AuthProvider) {
        return this.angularFireAuth.signInWithPopup(provider);
    }*/

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any>
    {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean>
    {
        // Check if the user is logged in
        if ( this._authenticated )
        {


          return of(true);
        }

        // Check the access token availability
        if ( !this.accessToken )
        {
            return of(false);
        }

        // Check the access token expire date
        if ( AuthUtils.isTokenExpired(this.accessToken) )
        {
            return of(false);
        }

        // If the access token exists and it didn't expire, sign in using it
        return this.signInUsingToken();
    }
}
